import React from 'react';
import { Box, CardActionArea, Stack, Typography, Tooltip } from '@mui/material';
import {
  ArrowForwardRounded,
  FolderOutlined,
  GraphicEqRounded,
  LayersOutlined,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { GetProjectsQueryData } from '@/generated/graphql';
import { SpotlightCard } from '@/common/components/reactBits';
import { ResourceIcon } from '@/common/components/ui/LibraryPrimitives';
import { AudioPlayer } from '@/common/components/projectDetails/components/AudioPlayer';
import {
  projectCardDetails,
  projectCardUpdated,
} from '../utils/projectCardDetails';

type ProjectListItem = GetProjectsQueryData['projects'][number];

const ProjectCard: React.FC<{ project: ProjectListItem }> = ({ project }) => {
  const location = useLocation();
  const details = projectCardDetails(project.description);
  const versionCount =
    project.versionCount ?? project.versionHistory?.length ?? 0;
  const preview = project.previewFile;

  return (
    <SpotlightCard>
      <Box
        data-testid={`recent-project-${project._id}`}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <CardActionArea
          component={RouterLink}
          to={`/projects/${encodeURIComponent(project._id)}`}
          state={{ from: `${location.pathname}${location.search}` }}
          aria-label={`Open ${project.name}`}
          sx={{
            p: 2.25,
            pb: 1.5,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 },
            '&:hover .project-open-arrow, &.Mui-focusVisible .project-open-arrow':
              { color: 'primary.main' },
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.25} mb={1.75}>
            <ResourceIcon sx={{ width: 34, height: 34, borderRadius: 1 }}>
              {details.icon ? (
                <Box
                  component="img"
                  src={details.icon}
                  alt=""
                  sx={{ width: 22, height: 22, objectFit: 'contain' }}
                />
              ) : (
                <FolderOutlined fontSize="small" />
              )}
            </ResourceIcon>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1, minWidth: 0 }}
            >
              {details.label}
            </Typography>
            <ArrowForwardRounded
              className="project-open-arrow"
              sx={{ fontSize: 18, color: 'text.secondary' }}
            />
          </Stack>
          <Typography
            component="h3"
            title={project.name}
            sx={{
              fontSize: '1.05rem',
              fontWeight: 700,
              lineHeight: 1.35,
              letterSpacing: '-0.015em',
              minHeight: '2.7em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {project.name}
          </Typography>
          {details.note && (
            <Typography
              variant="body2"
              color="text.secondary"
              title={details.note}
              sx={{
                mt: 0.5,
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {details.note}
            </Typography>
          )}
        </CardActionArea>
        <Box sx={{ px: 2.25, pb: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            gap={0.75}
            sx={{ mb: 1.5, color: 'text.secondary' }}
          >
            <LayersOutlined sx={{ fontSize: 14 }} />
            <Typography variant="caption">
              {versionCount} {versionCount === 1 ? 'version' : 'versions'}
            </Typography>
            <Typography variant="caption" aria-hidden="true">
              ·
            </Typography>
            <Tooltip title={new Date(project.updatedAt).toLocaleString()}>
              <Typography variant="caption">
                {projectCardUpdated(project.updatedAt)}
              </Typography>
            </Tooltip>
          </Stack>
          {preview?.waveForm ? (
            <AudioPlayer
              waveForm={preview.waveForm}
              projectId={project._id}
              fileId={preview._id}
              variant="inline"
              label={`preview for ${project.name}`}
            />
          ) : (
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{
                height: 38,
                borderTop: 1,
                borderColor: 'divider',
                color: 'text.secondary',
              }}
            >
              <GraphicEqRounded sx={{ fontSize: 17 }} />
              <Typography variant="caption">No audio preview yet</Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </SpotlightCard>
  );
};
export default ProjectCard;
